import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Backdrop,
  CircularProgress,
  Modal,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Fade,
  Stack,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import backend from "../../../../../api";
import Classes from "./MentorViewProfile.module.css";

import {
  FaBriefcase,
  FaBuilding,
  FaGraduationCap,
  FaHandsHelping,
  FaCalendarAlt,
  FaGlobe,
  FaLinkedinIn,
} from "react-icons/fa";
import { BsCreditCardFill } from "react-icons/bs";
import { BiSolidContact } from "react-icons/bi";
import { IoBriefcase, IoShieldHalfOutline } from "react-icons/io5";
import { FaMedal } from "react-icons/fa6";
import { FaLanguage } from "react-icons/fa6";
import { IoPeople } from "react-icons/io5";

import { ArrowLeftIcon, StarFilledIcon } from "../../../../../utils/Icons";

const PRIMARY = "#f26a63";

const muiTheme = createTheme({
  palette: {
    primary: { main: PRIMARY },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: PRIMARY,
          color: "#fff",
          "&:hover": { backgroundColor: "#e45a53" },
        },
        outlinedPrimary: {
          borderColor: PRIMARY,
          color: PRIMARY,
          "&:hover": { borderColor: PRIMARY },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: PRIMARY,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: { color: PRIMARY },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "rgba(242,106,99,0.12)",
          },
          "&.Mui-selected.Mui-focusVisible, &.Mui-selected:hover": {
            backgroundColor: "rgba(242,106,99,0.18)",
          },
        },
      },
    },
  },
});

const genMeetLink = ({ roomPrefix = "mentr", subject, displayName } = {}) => {
  const rnd = () => Math.random().toString(36).slice(2, 10);
  const room = `${roomPrefix}-${rnd()}`;
  const base = `https://meet.jit.si/${encodeURIComponent(room)}`;

  const parts = [
    "config.disableDeepLinking=true",
    "config.prejoinConfig.enabled=true",
  ];
  if (subject) parts.push(`config.subject=${encodeURIComponent(subject)}`);
  if (displayName)
    parts.push(
      `userInfo.displayName=${encodeURIComponent(JSON.stringify(displayName))}`
    );

  return `${base}#${parts.join("&")}`;
};

function addMinutes(time, minutes) {
  const [hours, mins] = time.split(":").map(Number);
  let total = hours * 60 + mins + minutes;
  const newHours = Math.floor(total / 60) % 24;
  const newMins = total % 60;
  return `${String(newHours).padStart(2, "0")}:${String(newMins).padStart(2, "0")}`;
}

function BookingModal({ open, onClose, mentor, user, onBook }) {
  const [duration, setDuration] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");

  const formatDateMDY = (isoDate) => {
    const d = new Date(isoDate);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };
  const formatTime = (time) => {
    const [h, m] = time.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${period}`;
  };


  const availability = mentor?.mentorProfile?.availability || {};
  const timeSettings = availability.timeSettings || {};
  const availableSlots = availability.availableSlots || {};
  const durations = timeSettings.durations || [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dates = Object.keys(availableSlots)
    .filter((d) => !isNaN(Date.parse(d)) && new Date(d) >= today)
    .sort((a, b) => new Date(a) - new Date(b));

    // ✅ hide passed slots only for "today"
const isPastSlotForSelectedDate = (selectedDate, slotHHMM) => {
  if (!selectedDate || !slotHHMM) return false;

  const now = new Date();

  const [y, m, d] = selectedDate.split("-").map(Number);
  const [hh, mm] = slotHHMM.split(":").map(Number);

  const slotDateTime = new Date(y, m - 1, d, hh, mm, 0, 0);

  const isToday =
    slotDateTime.getFullYear() === now.getFullYear() &&
    slotDateTime.getMonth() === now.getMonth() &&
    slotDateTime.getDate() === now.getDate();

  if (!isToday) return false;

  return slotDateTime <= now; // passed (or equal) => hide
};


const visibleSlots =
  date && duration
    ? Object.keys(availableSlots?.[date]?.[duration] || {})
        .filter((k) => availableSlots[date][duration][k] === "available")
        // ✅ hide passed time slots for today's date
        .filter((k) => !isPastSlotForSelectedDate(date, k))
        .sort((a, b) => a.localeCompare(b))
    : [];


  const handleDurationChange = (e) => {
    setDuration(e.target.value);
    setDate("");
    setSlot("");
  };
  const handleDateChange = (e) => {
    setDate(e.target.value);
    setSlot("");
  };

  const handleBook = () => {
    if (!duration || !date || !slot) return;
    const selectedDuration = durations.find(
      (d) => Number(d.time) === Number(duration)
    );
    const end = addMinutes(slot, Number(selectedDuration.time));

    const booking = {
      userId: user?._id,
      mentorId: mentor?._id,
      date,
      start: slot,
      end,
      duration: Number(selectedDuration.time),
      // IMPORTANT: this "price" is mentor fee only (backend adds $5 and fees)
      price: Number(selectedDuration.price),
      meetLink: genMeetLink(),
      source: "mentor_profile",
    };

    onBook(booking);
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open} timeout={180}>
        <Box sx={sxModal}>
          <Box sx={sxModalHeader}>
            <div>
              <Typography variant="h8" sx={{ fontWeight: 700, lineHeight: 1 }}>
                Book a Meeting
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                Pick duration, date, then choose an available time.
              </Typography>
            </div>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Divider />

          <div style={{ padding: "16px", maxHeight: "80vh", overflowY: "auto" }}>
            <Typography sx={labelText}>Select Duration</Typography>
            <Select value={duration} onChange={handleDurationChange} fullWidth size="small">
              <MenuItem value="">Select Duration</MenuItem>
              {durations.map((d, i) => (
                <MenuItem key={i} value={d.time}>
                  {d.time} min — ${d.price}
                </MenuItem>
              ))}
            </Select>

            {duration && (
              <>
                <Typography sx={{ ...labelText, mt: 2 }}>Select Date</Typography>
                <Select value={date} onChange={handleDateChange} fullWidth size="small">
                  <MenuItem value="">Select Date</MenuItem>
                  {dates.map((d) => (
                    <MenuItem key={d} value={d}>
                      {formatDateMDY(d)}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {date && (
              <>
                <Typography sx={{ ...labelText, mt: 2 }}>Select Slot</Typography>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {visibleSlots.length ? (
                    visibleSlots.map((s) => (
                      <Button
                        key={s}
                        onClick={() => setSlot(s)}
                        variant={slot === s ? "contained" : "outlined"}
                        size="small"
                        sx={{ textTransform: "none" }}
                      >
                        {formatTime(s)}
                      </Button>
                    ))
                  ) : (
                    <Typography sx={{ gridColumn: "1 / -1", fontSize: 13, color: "#777" }}>
                      No available slots for this date.
                    </Typography>
                  )}
                </div>
              </>
            )}
          </div>

          <Divider />

          <div style={{ padding: "16px" }}>
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              <Button onClick={onClose} variant="outlined" fullWidth>
                Close
              </Button>
              <Button onClick={handleBook} disabled={!slot} variant="contained" fullWidth>
                {duration && date && slot ? "Review Booking" : "Next"}
              </Button>
            </Stack>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

function BookingConfirmationModal({ open, onClose, booking, onPay, paying }) {
  if (!booking) return null;

  const toMDY = (iso) => {
    const d = new Date(iso);
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${d.getFullYear()}`;
  };
  const to12h = (time) => {
    const [h, m] = time.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${period}`;
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open} timeout={180}>
        <Box sx={sxModal}>
          <Box sx={sxModalHeader}>
            <div>
              <Typography variant="h8" sx={{ fontWeight: 700, lineHeight: 1 }}>
                Booking Preview
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                Confirm the details before continuing to payment.
              </Typography>
            </div>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Divider />

          <div style={{ padding: "16px" }}>
            <SummaryRow label="Date" value={toMDY(booking?.date)} />
            <SummaryRow label="Time" value={`${to12h(booking?.start)} — ${to12h(booking?.end)}`} />
            <SummaryRow label="Duration" value={`${booking?.duration} min`} />
            <SummaryRow label="Mentor Fee" value={`$${booking?.price}`} />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
              Note: A $5 call fee is added at checkout.
            </Typography>
          </div>

          <Divider />

          <Box sx={sxModalActions}>
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              <Button onClick={onClose} variant="outlined" fullWidth>
                Close
              </Button>
              <Button variant="contained" fullWidth onClick={() => onPay(booking)} disabled={!!paying}>
                {paying ? "Redirecting…" : "Continue to Payment"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

function SummaryRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{value}</Typography>
    </Box>
  );
}

export default function ViewProfile() {
  const navigateTo = useNavigate();
  const { id } = useParams();
  const { user, token } = useSelector((s) => s.auth);

  const [mentor, setMentor] = useState(null);
  const [loading, setLoading] = useState(true);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    backend
      .get(`/api/user/${id}`)
      .then(({ data }) => setMentor(data.data))
      .finally(() => setLoading(false));
  }, [id]);

  const p = mentor?.mentorProfile || {};
  const basic = p?.basicInfo || {};
  const exp = p?.experience || {};
  const session = p?.sessionDetails || {};
  const bookingSetup = p?.bookingSetup || {};
  const linkedinUrl = p?.experience?.linkedinUrl || null;
  const portfolioUrl = p?.experience?.portfolioUrl || null;
  const resume = p?.experience?.resume || null;

  const ratingVal = Number(bookingSetup?.rating);
  const ratingsCount = Number(bookingSetup?.ratingsReceived || 0);
  const hasRating = Number.isFinite(ratingVal);

  const durations = mentor?.mentorProfile?.availability?.timeSettings?.durations || [];
  const durationRates = Array.isArray(durations)
    ? durations
        .filter((d) => d && d.time != null && d.price != null)
        .map((d) => ({ time: Number(d.time), price: Number(d.price) }))
        .filter((d) => Number.isFinite(d.time) && Number.isFinite(d.price))
        .sort((a, b) => a.time - b.time)
    : [];

  const rateText = durationRates?.length
    ? durationRates?.map((d) => `${d.time} min — $${d.price}`).join(" • ")
    : "—";

  const mentorStripeConnected = !!mentor?.mentorProfile?.isStripeConnected;

  const openBooking = () => {
    // HARD RULE: if stripe is not connected => no booking
    if (!mentorStripeConnected) {
      alert("This mentor has not connected Stripe yet, so bookings are currently disabled.");
      return;
    }
    setConfirmationOpen(false);
    setSuccessBooking(null);
    setBookingOpen(true);
  };

  const closeBooking = () => setBookingOpen(false);

  const handleBook = (booking) => {
    setBookingOpen(false);
    setSuccessBooking(booking);
    setConfirmationOpen(true);
  };

  const closeConfirmation = () => setConfirmationOpen(false);

  const handlePayment = async (booking) => {
    try {
      setPaymentLoading(true);

      const payload = {
        metadata: {
          userId: String(booking?.userId),
          mentorId: String(booking?.mentorId),
          date: String(booking?.date),
          start: String(booking?.start),
          end: String(booking?.end),
          duration: String(booking?.duration),
          meetLink: String(booking?.meetLink || ""),
          source: "mentor_profile",

          // IMPORTANT: "price" here = mentor fee (backend adds $5 & fees)
          price: String(booking?.price),
          currency: "usd",
        },
      };

      const resp = await backend.post("/pay/create-checkout-session", payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (resp?.data?.url) {
        window.location.assign(resp.data.url);
      } else {
        alert("Could not start payment.");
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      alert("Payment error: " + msg);
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <>
        <Backdrop open={loading} sx={{ zIndex: 1301, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>

        {bookingOpen && mentor && (
          <BookingModal
            open={bookingOpen}
            onClose={closeBooking}
            mentor={mentor}
            user={user}
            onBook={handleBook}
          />
        )}

        {confirmationOpen && successBooking && (
          <BookingConfirmationModal
            open={confirmationOpen}
            onClose={closeConfirmation}
            booking={successBooking}
            onPay={handlePayment}
            paying={paymentLoading}
          />
        )}

        {!loading && mentor && (
          <div className={Classes.hero}>
            <div className={Classes.header}>
              <button className={Classes.backBtn} onClick={() => navigateTo(-1)} aria-label="Back">
                <ArrowLeftIcon height="20px" fill="#888787" />
              </button>
              <h3>Profile</h3>
            </div>

            <div className={Classes.profileContainer}>
              <aside className={Classes.leftPanel}>
                <div className={Classes.photo}>
                  <img
                    src={basic?.avatar || "/assets/images/user.png"}
                    alt={mentor?.fullname || "Profile photo"}
                    loading="lazy"
                    decoding="async"
                    width={1600}
                    height={2000}
                    draggable={false}
                  />
                </div>

                <div className={Classes.flexAlign7}>
                  <h3 className={Classes.name}>{basic?.name}</h3>

                  {hasRating && (
                    <div className="flexAlign5" style={{ gap: 6 }}>
                      <StarFilledIcon height="15px" fill="var(--primary-color)" />
                      <h5 className={Classes.rating}>{ratingVal.toFixed(1)}</h5>
                      {ratingsCount > 0 && (
                        <h5 className={Classes.rating} style={{ color: "#777", marginLeft: 2 }}>
                          ({ratingsCount})
                        </h5>
                      )}
                    </div>
                  )}
                </div>

                <ul className={Classes.infoList}>
                  <li className={Classes.infoItem}>
                    <FaBriefcase className={Classes.icon} />
                    <div className={Classes.textBlock}>
                      <h5 className={Classes.label}>Position</h5>
                      <h5 className={Classes.value}>{basic.title || "—"}</h5>
                    </div>
                  </li>

                  <li className={Classes.infoItem}>
                    <FaBuilding className={Classes.icon} />
                    <div className={Classes.textBlock}>
                      <h5 className={Classes.label}>Company</h5>
                      <h5 className={Classes.value}>{basic.company || "—"}</h5>
                    </div>
                  </li>

                  <li className={Classes.infoItem}>
                    <FaGraduationCap className={Classes.icon} />
                    <div className={Classes.textBlock}>
                      <h5 className={Classes.label}>Education</h5>
                      <h5 className={Classes.value}>{basic.education || "—"}</h5>
                    </div>
                  </li>

                  <li className={Classes.infoItem}>
                    <FaHandsHelping className={Classes.icon} />
                    <div className={Classes.textBlock}>
                      <h5 className={Classes.label}>Clubs/Charities</h5>
                      <h5 className={Classes.value}>{basic.clubs || "—"}</h5>
                    </div>
                  </li>

                  <li className={Classes.infoItem}>
                    <BsCreditCardFill className={Classes.icon} />
                    <div className={Classes.textBlock}>
                      <h5 className={Classes.label}>Rate</h5>
                      <h5 className={Classes.value}>{rateText}</h5>
                    </div>
                  </li>

                  <li className={Classes.infoItem}>
                    <FaCalendarAlt className={Classes.icon} />
                    <div className={Classes.textBlock}>
                      <h5 className={Classes.label}>Availability</h5>
                      <h5 className={Classes.value}>
                        {(session.availability || []).join(", ") || "—"}
                      </h5>
                    </div>
                  </li>
                </ul>

                <button
                  className={Classes.messageBtn}
                  onClick={openBooking}
                  disabled={!mentorStripeConnected}
                  style={{
                    opacity: mentorStripeConnected ? 1 : 0.55,
                    cursor: mentorStripeConnected ? "pointer" : "not-allowed",
                  }}
                >
                  Book meeting
                </button>

                {!mentorStripeConnected && (
                  <p style={{ marginTop: 10, fontSize: 12, color: "#b00020" }}>
                    Booking disabled: Mentor hasn’t connected Stripe yet.
                  </p>
                )}
              </aside>

              <div className={Classes.rightPanel}>
                <section className={Classes.section}>
                  <header className={Classes.sectionHeader}>
                    <h3>Personal Bio</h3>
                    <div className={Classes.sectionActions}>
                      {resume && (
                        <a href={resume} target="_blank" rel="noreferrer">
                          <BiSolidContact />
                        </a>
                      )}
                      {portfolioUrl && (
                        <a href={portfolioUrl} target="_blank" rel="noreferrer">
                          <FaGlobe />
                        </a>
                      )}
                      {linkedinUrl && (
                        <a href={linkedinUrl} target="_blank" rel="noreferrer">
                          <FaLinkedinIn />
                        </a>
                      )}
                    </div>
                  </header>
                  <p>{basic.bio || "—"}</p>
                </section>

                <section className={Classes.section}>
                  <h3 className={Classes.sectionTitle}>Professional Experience</h3>

                  <div className={Classes.subItem}>
                    <IoBriefcase className={Classes.icon} />
                    <div>
                      <h4 className={Classes.subItemTitle}>Areas of Expertise</h4>
                      <p className={Classes.value}>{(exp.expertise || []).join(", ") || "—"}</p>
                    </div>
                  </div>

                  <div className={Classes.subItem}>
                    <FaMedal className={Classes.icon} />
                    <div>
                      <h4 className={Classes.subItemTitle}>Level of Experience</h4>
                      <p className={Classes.value}>{(exp.levels || []).join(", ") || "—"}</p>
                    </div>
                  </div>
                </section>

                <section className={Classes.section}>
                  <h3 className={Classes.sectionTitle}>Other Details</h3>

                  <div className={Classes.subItem}>
                    <IoPeople className={Classes.icon} />
                    <div>
                      <h4 className={Classes.subItemTitle}>What Can Mentees Expect?</h4>
                      <p className={Classes.value}>{session.expectSession || "—"}</p>
                    </div>
                  </div>

                  {p?.experience?.howHelp && p?.experience?.howHelp?.length > 0 && (
                    <div className={Classes.subItem}>
                      <IoShieldHalfOutline className={Classes.icon} />
                      <div>
                        <h4 className={Classes.subItemTitle}>A Great Match for</h4>
                        <p className={Classes.value}>
                          {(p?.experience?.howHelp || []).slice(0, 3).map((t) => (
                            <li key={t}>{t}</li>
                          ))}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className={Classes.subItem}>
                    <FaLanguage className={Classes.icon} />
                    <div>
                      <h4 className={Classes.subItemTitle}>Languages</h4>
                      <p className={Classes.value}>{(session.languagesSpoken || []).join(", ") || "—"}</p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        )}
      </>
    </ThemeProvider>
  );
}

const sxModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 460,
  maxWidth: "calc(100vw - 32px)",
  bgcolor: "background.paper",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  borderRadius: 3,
  overflow: "hidden",
};

const sxModalHeader = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  p: 2,
};

const sxModalActions = {
  p: 2,
  pt: 1.5,
};

const labelText = { fontSize: 12, color: "text.secondary", mb: 0.5 };
